'use client';

import { useEffect, useRef } from 'react';

/**
 * Dynamic Favicon Component - Atom Design Pattern
 *
 * A component that dynamically sets the favicon from Strapi data
 * Updates the favicon link in the document head
 * Hydration-safe implementation with robust error handling
 */

export interface DynamicFaviconProps {
  faviconUrl?: string;
  fallbackUrl?: string;
}

const DynamicFavicon = ({
  faviconUrl,
  fallbackUrl = '/favicon.ico',
}: DynamicFaviconProps) => {
  // Use ref to track if component is still mounted
  const isMountedRef = useRef(true);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const currentFaviconRef = useRef<HTMLLinkElement | null>(null);

  useEffect(() => {
    // Check if we're in a browser environment and document is available
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Add a small delay to ensure DOM is ready and hydration is complete
    timeoutIdRef.current = setTimeout(() => {
      // Check if component is still mounted before proceeding
      if (!isMountedRef.current) return;

      try {
        const targetUrl = faviconUrl || fallbackUrl;

        // Check if we already have the correct favicon
        if (
          currentFaviconRef.current &&
          currentFaviconRef.current.href === targetUrl
        ) {
          return; // Already set correctly
        }

        // Create new favicon link
        const newLink = document.createElement('link');
        newLink.type = 'image/x-icon';
        newLink.rel = 'shortcut icon';
        newLink.href = targetUrl;
        newLink.id = 'dynamic-favicon'; // Add ID for easier management

        // Safer approach: Update existing favicon instead of removing/adding
        const existingFavicon = document.getElementById(
          'dynamic-favicon'
        ) as HTMLLinkElement;

        if (existingFavicon) {
          // Update existing favicon
          existingFavicon.href = targetUrl;
          currentFaviconRef.current = existingFavicon;
        } else {
          // Remove any existing favicon links more safely
          const existingLinks = document.querySelectorAll("link[rel*='icon']");

          // Only remove links that are not our dynamic favicon
          existingLinks.forEach(existingLink => {
            try {
              // Only remove if it's not our dynamic favicon and still exists
              if (
                existingLink &&
                existingLink.parentNode &&
                document.contains(existingLink) &&
                existingLink.id !== 'dynamic-favicon'
              ) {
                existingLink.parentNode.removeChild(existingLink);
              }
            } catch (removeError) {
              // Silently handle removal errors - element might have been removed by React
              console.warn(
                'Error removing existing favicon link:',
                removeError
              );
            }
          });

          // Add new favicon link only if component is still mounted
          if (isMountedRef.current && document.head) {
            document.head.appendChild(newLink);
            currentFaviconRef.current = newLink;
          }
        }
      } catch (error) {
        // Silently handle favicon errors to prevent breaking the app
        console.warn('Error setting favicon:', error);
      }
    }, 200); // Longer delay to ensure hydration is complete

    // Cleanup function
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;

      // Clear timeout if it exists
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      // Clear the ref
      currentFaviconRef.current = null;
    };
  }, [faviconUrl, fallbackUrl]);

  return null; // This component doesn't render anything
};

export default DynamicFavicon;
